/**
 * Lifecycle interfaces — shared between server and client.
 * DO NOT EDIT in server/src/shared/common/ or phaser/src/shared/common/
 * Edit the canonical copy in shared/ and run: node scripts/sync-shared.js
 */

/**
 * Interface for objects that hold resources requiring cleanup.
 * Implement this to ensure proper cleanup of event listeners, timers, etc.
 */
export interface IDisposable {
  /**
   * Release all resources held by this object.
   * After calling dispose(), the object should not be used.
   */
  dispose(): void;

  /**
   * Whether this object has been disposed.
   * Implementations should check this before performing operations.
   */
  readonly isDisposed: boolean;
}

/**
 * Interface for objects that require async initialization.
 */
export interface IInitializable {
  /**
   * Perform async initialization.
   * This should be called once before the object is used.
   */
  initialize(): Promise<void>;

  /**
   * Whether this object has been initialized.
   */
  readonly isInitialized: boolean;
}

/**
 * Combined interface for objects with full lifecycle management.
 * Use this for services and managers that need both init and cleanup.
 */
export interface ILifecycle extends IDisposable, IInitializable {}

/**
 * Simple disposable function type.
 * Useful for cleanup callbacks from event subscriptions.
 */
export type DisposeFn = () => void;

/**
 * Options for DisposableGroup.add()
 */
export interface AddDisposableOptions {
  /** Optional name for debugging */
  name?: string;
  /** Priority for disposal order (higher = disposed first) */
  priority?: number;
}

/**
 * Utility class to manage multiple disposables as a group.
 * When the group is disposed, all registered disposables are disposed
 * in reverse registration order (LIFO), or by priority if specified.
 */
export class DisposableGroup implements IDisposable {
  private disposables: Array<{
    disposable: IDisposable | DisposeFn;
    name?: string;
    priority: number;
  }> = [];
  private _isDisposed = false;

  get isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Add a disposable to the group.
   * @param disposable - IDisposable object or cleanup function
   * @param options - Optional name and priority
   * @returns A function to remove this specific disposable
   */
  add(
    disposable: IDisposable | DisposeFn,
    options: AddDisposableOptions = {}
  ): DisposeFn {
    if (this._isDisposed) {
      // Already disposed, immediately dispose the new item
      this.disposeOne(disposable);
      return () => {};
    }

    const entry = {
      disposable,
      name: options.name,
      priority: options.priority ?? 0,
    };
    this.disposables.push(entry);

    // Return a removal function
    return () => {
      const index = this.disposables.indexOf(entry);
      if (index !== -1) {
        this.disposables.splice(index, 1);
      }
    };
  }

  /**
   * Add a cleanup function to the group.
   * Convenience method for add() with a function.
   */
  addCleanup(fn: DisposeFn, name?: string): DisposeFn {
    return this.add(fn, { name });
  }

  /**
   * Dispose all registered disposables.
   * Higher priority items are disposed first.
   * Within same priority, LIFO order.
   */
  dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;

    // Sort by priority (descending), then reverse for LIFO within same priority
    const sorted = [...this.disposables].sort(
      (a, b) => b.priority - a.priority
    );

    for (const entry of sorted) {
      try {
        this.disposeOne(entry.disposable);
      } catch (error) {
        console.error(
          `Error disposing ${entry.name ?? 'unnamed'}:`,
          error
        );
      }
    }

    this.disposables = [];
  }

  private disposeOne(disposable: IDisposable | DisposeFn): void {
    if (typeof disposable === 'function') {
      disposable();
    } else {
      disposable.dispose();
    }
  }

  /**
   * Number of registered disposables.
   */
  get size(): number {
    return this.disposables.length;
  }
}

/**
 * Constructor type for mixin classes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Constructor<T = object> = new (...args: any[]) => T;

/**
 * Mixin helper to add disposal tracking to a class.
 * Usage:
 *   class MyManager extends withDisposable(BaseClass) { ... }
 */
export function withDisposable<TBase extends Constructor>(Base: TBase) {
  return class WithDisposable extends Base implements IDisposable {
    /** @internal */
    readonly _disposables = new DisposableGroup();
    /** @internal */
    _disposed = false;

    get isDisposed(): boolean {
      return this._disposed;
    }

    dispose(): void {
      if (this._disposed) return;
      this._disposed = true;
      this._disposables.dispose();
    }

    /**
     * Add a disposable to be cleaned up when this object is disposed.
     */
    addDisposable(disposable: IDisposable | DisposeFn, name?: string): DisposeFn {
      return this._disposables.add(disposable, { name });
    }
  };
}
